
import { useMemo, useState } from 'react';
import { useGetProcurements } from '@/hooks/use-procurement';
import { Procurement } from '@/types/database.types';
import { useAuth } from '@/contexts/AuthContext';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import { calculateTotalAmount } from '@/utils/procurementCalculations';

export type SortConfig = { key: string; direction: 'asc' | 'desc' };
export type ProcurementStatus = 
  | 'pending_finance' 
  | 'pending'
  | 'initiated'
  | 'awaiting_approval'
  | 'price_confirmed' 
  | 'quality_confirmed' 
  | 'agreement_signed' 
  | 'pickup_scheduled' 
  | 'harvested' 
  | 'in_transit' 
  | 'delivered' 
  | 'completed'
  | 'rejected';

export type FilterOptions = {
  status: ProcurementStatus | 'all';
  searchTerm: string;
};

const defaultFilterOptions: FilterOptions = {
  status: 'all',
  searchTerm: ''
};

interface UseSellerProcurementsProps {
  defaultSortConfig?: SortConfig;
}

export const useSellerProcurements = ({
  defaultSortConfig = { key: 'created_at', direction: 'desc' }
}: UseSellerProcurementsProps = {}) => {
  const { data: allProcurements, isLoading } = useGetProcurements();
  const { user } = useAuth();
  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSortConfig);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(defaultFilterOptions);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredAndSortedProcurements = useMemo(() => {
    if (!allProcurements || !user) return [];
    
    // Filter to only show current user's procurements
    let filtered = allProcurements.filter(p => p.requestor_id === user.id);
    
    // Apply status filter
    if (filterOptions.status !== 'all') {
      filtered = filtered.filter(p => p.status === filterOptions.status);
    }
    
    // Apply search filter
    if (filterOptions.searchTerm) {
      const term = filterOptions.searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        (p.product_name && p.product_name.toLowerCase().includes(term)) ||
        (p.source_type && p.source_type.toLowerCase().includes(term)) ||
        (p.id && p.id.toLowerCase().includes(term))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key === 'created_at') {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortConfig.key === 'total_amount') {
        const amountA = calculateTotalAmount(a);
        const amountB = calculateTotalAmount(b);
        return sortConfig.direction === 'asc' ? amountA - amountB : amountB - amountA;
      }
      
      if (sortConfig.key === 'status') {
        const statusA = a.status || '';
        const statusB = b.status || '';
        return sortConfig.direction === 'asc' 
          ? statusA.localeCompare(statusB) 
          : statusB.localeCompare(statusA);
      }
      
      if (sortConfig.key === 'product_name') {
        const nameA = a.product_name || '';
        const nameB = b.product_name || '';
        return sortConfig.direction === 'asc' 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      }
      
      return 0;
    });
    
    return filtered;
  }, [allProcurements, user, filterOptions, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedProcurements.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProcurements = filteredAndSortedProcurements.slice(startIndex, startIndex + pageSize);

  const handleSort = (key: string) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const resetFilters = () => {
    setFilterOptions(defaultFilterOptions);
    setCurrentPage(1);
  };

  const setSearchTerm = (searchTerm: string) => {
    setFilterOptions(prev => ({ ...prev, searchTerm }));
    setCurrentPage(1);
  };

  const setStatus = (status: ProcurementStatus | 'all') => {
    setFilterOptions(prev => ({ ...prev, status }));
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    procurements: paginatedProcurements,
    isLoading,
    sortConfig,
    handleSort,
    filterOptions,
    setSearchTerm,
    setStatus,
    resetFilters,
    currentPage,
    totalPages,
    pageSize,
    setPageSize,
    goToPage,
    totalCount: filteredAndSortedProcurements.length
  };
};
